import {
  ListItem,
  SvgIcon,
  Paper,
  BottomNavigationAction,
  Menu,
  useMediaQuery,
} from '@mui/material'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import React, { forwardRef, memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MuiBottomNavigation from '@mui/material/BottomNavigation'
import { useLocation } from 'react-router-dom'
import { styled } from '@mui/material/styles'

import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '@hapana/shared/web/components/shared/button/button.constants'
import { NAVIGATION_LABEL } from '@hapana/shared/web/components/shared/navigation/navigation.constant'
import PlusIcon from '@hapana/shared/web/styles/assets/svgs/plus-small.svg'
import ExchangeIcon from '@hapana/shared/web/styles/assets/svgs/exchange.svg'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import {
  AVATAR_SIZE,
  AVATAR_TYPES,
  AVATAR_VARIANT,
} from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import Avatar from '@hapana/shared/web/components/shared/avatar/single-avatar.component'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'

import { styles } from '../navigation.styles'
// These dependencies are now injected as props to avoid circular dependencie

import GotoLocations from './modals/goto-location.component'

const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'sx' || typeof prop !== 'object',
})(() => ({}))

const BottomNavigation = forwardRef(
  (
    {
      isLocation,
      setRoute,
      image,
      bottomNavItems,
      hasRouteIntegration,
      pathname,
      shouldShowAddAdminLink,
      // Auth and user data injected to avoid circular dependencies
      authState = { isLoadingAuth: false, hasValidSession: false },
      userData = { data: {}, isLoading: false },
      // Optional components that can be injected
      AddEditCustomAdminLinksComponent = null,
      topBannerHeight = 0,
      onItemClick,
    },
    ref
  ) => {
    const [value, setValue] = useState(0)
    const [selected, setSelected] = useState(hasRouteIntegration ? {} : { home: true })
    const { t } = useTranslation(['common'])
    const { pushDialog } = useDialog()
    const [anchorEl, setAnchorEl] = useState(null)
    const location = useLocation()
    // Use injected auth and user data instead of hooks to avoid circular dependencies
    const { isLoadingAuth, hasValidSession } = authState
    const { data, isLoading } = userData

    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))

    const goTo = (item) => {
      // If an onItemClick handler is provided and returns true, skip default routing
      if (onItemClick && onItemClick(item)) {
        setAnchorEl(null)
        return
      }

      if (hasRouteIntegration && item.href) {
        setRoute?.(item.href)
        setAnchorEl(null)
      } else if (item.url) {
        // Noopener is an option to prevent security issues
        window.open(item.url, '_blank', 'noopener')
      }

      setSelected({ [item.id]: true })
    }

    const handleMenuOpen = (event) => {
      setAnchorEl(event?.currentTarget)
    }

    const openanchorEl = Boolean(anchorEl)

    const newSaleOnOpen = useCallback(
      (item) => ({
        variant: BUTTON_VARIANTS.OUTLINED,
        label: item.label,
        startIcon: item.icon,
      }),
      []
    )

    if (isLoadingAuth || isLoading) {
      return (
        <Box sx={styles.loadingRoot}>
          <Loading />
        </Box>
      )
    }

    const navItem = isLocation ? bottomNavItems?.withLocation : bottomNavItems?.withoutLocation

    const newBottomNavItems = {
      ...navItem,
      userProfile: {
        ...navItem.userProfile,
        src: data?.me?.profileImage?.url,
      },
    }

    const ActionItem = newBottomNavItems?.actionItem?.component

    const isUserProfileSelected = pathname?.includes('/profile')

    return (
      <Paper sx={styles.root} elevation={3}>
        {hasValidSession && (
          <>
            <MuiBottomNavigation
              showLabels
              value={value}
              onChange={(_, newValue) => setValue(newValue)}
              sx={styles.bottomNavigation}
            >
              {/* Bottom NavItem is fixed with length 5 */}
              {(newBottomNavItems?.bottomNavItems || []).map((item) => {
                const isRoot = item.href === '/'
                const isActive =
                  hasRouteIntegration &&
                  ((pathname?.includes(item?.href) && !isRoot) || (isRoot && pathname === '/'))

                return (
                  <BottomNavigationAction
                    key={item.id}
                    disabled={item?.grayedOut}
                    label={item.label}
                    icon={<SvgIcon sx={styles.menuIcon} component={item.icon} />}
                    sx={[styles.itemButton, isActive && styles.selected]}
                    onClick={(e) => (item?.id === 'More' ? handleMenuOpen(e) : goTo(item))}
                  />
                )
              })}
            </MuiBottomNavigation>
            <Menu
              anchorEl={anchorEl}
              open={openanchorEl}
              onClose={() => setAnchorEl(null)}
              slotProps={{
                paper: {
                  sx: {
                    ...styles.menuPaperStyles,
                    ...(topBannerHeight > 0 && isMobile && { paddingTop: `${topBannerHeight}px` }),
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box sx={styles.companyInfo}>
                {image?.openedSrc && (
                  <Box sx={styles.companyDetails} onClick={image?.onClick}>
                    <img
                      alt={
                        image?.alt ||
                        t('common:navigationLinks.logoAlt', { defaultValue: 'Brand logo' })
                      }
                      src={image.openedSrc}
                      style={styles.openedImage}
                    />
                  </Box>
                )}
                {bottomNavItems?.loc && (
                  <Box sx={styles.changeButtonBox}>
                    <Button
                      variant={BUTTON_VARIANTS.TEXT}
                      sx={styles.changeButtonStyles}
                      label={`Change`}
                      startIcon={ExchangeIcon}
                      onClick={() => {
                        setRoute('/location-selection')
                        setAnchorEl(null)
                      }}
                    />
                  </Box>
                )}
              </Box>
              <Divider />

              {/* Go to Location Functionality  */}

              {bottomNavItems?.loc?.map((item) => {
                return (
                  <Box sx={styles.brandMobile} key={item?.id}>
                    <Box sx={styles.brand}>
                      <SvgIcon sx={styles.menuIcon} component={item.icon} />
                      <ListItemText
                        primary={item.label}
                        sx={styles.itemLabelMobile}
                        onClick={() => {
                          pushDialog(GotoLocations, { setRoute })
                        }}
                      />
                    </Box>
                  </Box>
                )
              })}

              {bottomNavItems?.loc && <Divider />}

              {/* main navItemsList */}

              <List sx={styles.menuList}>
                <ListItem sx={styles.menuListTop}>
                  {newBottomNavItems?.main?.map((item) => {
                    const isRoot = item.href === '/'

                    const isActive =
                      hasRouteIntegration &&
                      ((pathname?.includes(item.href) && !isRoot) || (isRoot && pathname === '/'))
                    return (
                      <ListItemButton
                        test-id={item.label}
                        disabled={item?.grayedOut}
                        key={item.id}
                        sx={[styles.itemButton, (isActive || selected[item.id]) && styles.selected]}
                        onClick={() => goTo(item)}
                      >
                        <StyledListItemIcon
                          sx={typeof styles.itemIcon === 'object' ? styles.itemIcon : {}}
                        >
                          <SvgIcon sx={styles.menuIcon} component={item.icon} />
                        </StyledListItemIcon>
                        <ListItemText primary={item.label} sx={styles.bottomItemLabel} />
                      </ListItemButton>
                    )
                  })}

                  {newBottomNavItems?.main && (
                    <Divider sx={[styles.divider, styles.customDivider]} />
                  )}

                  {/* Quick Search List */}

                  {/* <>
                <Button
                  sx={{ backgroundColor: 'transparent', alignSelf: 'baseline', marginLeft: 1 }}
                  label={bottomNavItems?.quickSearch?.label}
                  size={BUTTON_SIZE.SMALL}
                  theme={BUTTON_THEME.SECONDARY}
                  variant={BUTTON_VARIANTS.OUTLINED}
                  startIcon={navItem?.quickSearch?.icon}
                  onClick={handleOpen}
                  test-id={bottomNavItems?.quickSearch?.label}
                />
                <Modal
                  open={openDialog}
                  onClose={handleClose}
                  aria-labelledby="modal-modal-title"
                  aria-describedby="modal-modal-description"
                >
                  {bottomNavItems?.quickSearch?.component}
                </Modal>
              </>
              {bottomNavItems?.quickSearch && (
                <Divider sx={[styles.divider, styles.customDivider]} />
              )} */}

                  {/* Cutom Pages List Functionality */}

                  {!isLocation && newBottomNavItems?.custom && (
                    <ListItemText sx={styles.customHeading}>
                      <Typography typography="small">
                        {t('common:navigationLinks.customPage', { count: 2 })}
                      </Typography>
                    </ListItemText>
                  )}
                  {newBottomNavItems?.custom && (
                    <ListItemText sx={styles.customHeading}>
                      <Typography typography="small">
                        {t('common:navigationLinks.customPage', { count: 2 })}
                      </Typography>
                    </ListItemText>
                  )}

                  <>
                    {newBottomNavItems?.custom?.map((item) => {
                      const isRoot = item.href === '/'

                      const isActive =
                        hasRouteIntegration &&
                        ((pathname?.includes(item.href) && !isRoot) || (isRoot && pathname === '/'))

                      return item.label === NAVIGATION_LABEL?.NEW_SALE ? (
                        <ListItemText key={item.id} sx={[styles.itemButton, styles.newSale]}>
                          <Button
                            size={BUTTON_SIZE.MEDIUM}
                            stroke={true}
                            theme={BUTTON_THEME.GRAY}
                            {...newSaleOnOpen(item)}
                          />
                        </ListItemText>
                      ) : (
                        <>
                          <ListItemButton
                            key={item.id}
                            disabled={item?.grayedOut}
                            sx={[
                              styles.itemButton,
                              (isActive || selected[item.id]) && styles.selected,
                            ]}
                          >
                            <StyledListItemIcon
                              sx={typeof styles.itemIcon === 'object' ? styles.itemIcon : {}}
                            >
                              <SvgIcon sx={styles.menuIcon} component={item.icon} />
                            </StyledListItemIcon>
                            <ListItemText primary={item.label} sx={styles.bottomItemLabel} />
                          </ListItemButton>
                        </>
                      )
                    })}
                    {newBottomNavItems?.custom && (
                      <Divider sx={[styles.divider, styles.customDivider]} />
                    )}
                    <ListItemText sx={styles.newLinkContainer}>
                      {shouldShowAddAdminLink && (
                        <Button
                          variant={BUTTON_VARIANTS.TEXT}
                          startIcon={PlusIcon}
                          label={t('common:navigationLinks.addNewLink')}
                          sx={styles.newLinkButton}
                          onClick={() => {
                            if (AddEditCustomAdminLinksComponent) {
                              pushDialog(AddEditCustomAdminLinksComponent)
                            }
                          }}
                        />
                      )}
                    </ListItemText>
                    {shouldShowAddAdminLink && (
                      <Divider sx={[styles.divider, styles.customDivider]} />
                    )}
                  </>

                  {/* Logout Functionality will define in future as per Reuirement*/}

                  {newBottomNavItems?.actionItem && <ActionItem />}

                  {newBottomNavItems?.actionItem && (
                    <Divider sx={[styles.divider, styles.customDivider]} />
                  )}

                  {newBottomNavItems?.bottomDefault?.map((item) => {
                    const isRoot = item.href === '/'
                    const isActive =
                      hasRouteIntegration &&
                      ((pathname?.includes(item.href) && !isRoot) || (isRoot && pathname === '/'))
                    return item.component ? (
                      item.component
                    ) : (
                      <ListItemButton
                        disabled={item?.grayedOut}
                        test-id={item.label}
                        key={item.id}
                        sx={[styles.itemButton, (isActive || selected[item.id]) && styles.selected]}
                        onClick={() => (item?.onClick() ? item?.onClick() : goTo(item))}
                      >
                        <StyledListItemIcon
                          sx={typeof styles.itemIcon === 'object' ? styles.itemIcon : {}}
                        >
                          <SvgIcon sx={styles.menuIcon} component={item.icon} />
                        </StyledListItemIcon>
                        <ListItemText primary={item.label} sx={styles.bottomItemLabel} />
                      </ListItemButton>
                    )
                  })}
                  <ListItemButton
                    sx={styles.itemButton}
                    onClick={() => {
                      setRoute(newBottomNavItems?.userProfile?.route)
                      setAnchorEl(null)
                    }}
                  >
                    <StyledListItemIcon
                      sx={typeof styles.itemIcon === 'object' ? styles.itemIcon : {}}
                    >
                      <Avatar
                        variant={AVATAR_VARIANT.CIRCULAR}
                        avatarType={AVATAR_TYPES.SINGLE_AVATAR}
                        size={AVATAR_SIZE.EXTRA_SMALL}
                        src={newBottomNavItems?.userProfile?.src}
                        alt={newBottomNavItems?.userProfile?.alt}
                      />
                    </StyledListItemIcon>
                    <ListItemText
                      primary={newBottomNavItems?.userProfile?.label}
                      sx={styles.bottomItemLabel}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Menu>
          </>
        )}
      </Paper>
    )
  }
)

BottomNavigation.propTypes = {
  isLocation: PropTypes.bool,
  setRoute: PropTypes.func,
  bottomNavItems: PropTypes.shape({
    loc: PropTypes.array,
    withLocation: PropTypes.object,
    withoutLocation: PropTypes.object,
    bottomDefault: PropTypes.array,
    userProfile: PropTypes.shape({
      route: PropTypes.string,
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
    quickSearch: PropTypes.shape({
      icon: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
      label: PropTypes.string,
      component: PropTypes.node,
    }),
  }),
  actionItem: PropTypes.shape({
    icon: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
    label: PropTypes.string,
    component: PropTypes.node,
  }),
  image: PropTypes.shape({
    openedSrc: PropTypes.string,
    closedSrc: PropTypes.string,
    alt: PropTypes.string,
    onClick: PropTypes.func,
  }),
  isOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  pathname: PropTypes.string,
  hasRouteIntegration: PropTypes.bool,
  addNewLinkAction: PropTypes.func,
  shouldShowAddAdminLink: PropTypes.bool,
  setisQuickSearch: PropTypes.func,
  // Props to avoid circular dependencies
  authState: PropTypes.shape({
    isLoadingAuth: PropTypes.bool,
    hasValidSession: PropTypes.bool,
  }),
  userData: PropTypes.shape({
    data: PropTypes.object,
    isLoading: PropTypes.bool,
  }),
  AddEditCustomAdminLinksComponent: PropTypes.elementType,
  topBannerHeight: PropTypes.number,
  onItemClick: PropTypes.func,
}

BottomNavigation.defaultProps = {
  isLocation: false,
  isOpen: false,
  onClick: () => null,
  // Default props to avoid circular dependencies
  authState: { isLoadingAuth: false, hasValidSession: false },
  userData: { data: {}, isLoading: false },
  AddEditCustomAdminLinksComponent: null,
  topBannerHeight: 0,
}

export default memo(BottomNavigation)
